import { not } from "@components/smart/FieldInfo";
import { getAddressFields, getAddressGroup, PhoneNumberDef } from "@components/smart/GeneralFieldDefinition";
import { createPath } from "@odata/BindingContext";
import {
    CompanyEntity,
    EntitySetName,
    EntityTypeName,
    PrCSSZSubmissionEmployeeEntity,
    PrHealthInsuranceCompanyEntity,
    PrHozChangeCodeEntity,
    PrHozSubmissionCompanyEntity,
    PrHozSubmissionDetailEntity,
    PrHozSubmissionEmployeeEntity,
    PrHozSubmissionEntity,
    PrHozSubmissionItemEntity
} from "@odata/GeneratedEntityTypes";
import { PrSubmissionTypeCode } from "@odata/GeneratedEnums";
import { setDefByEntityType } from "@pages/getDefByEntityType";
import { IDefinition, IGetDefinition } from "@pages/PageUtils";
import {
    getDefinitions as commonDef,
    getSubmissionFormDef,
    IGetSubmissionDefArgs,
    PrSubmissionGroup
} from "@pages/payroll/submission/Submission.def";
import { CSSZTranslationFiles } from "@pages/payroll/submission/submissionTypes/PrCSSZSubmission.utils";
import PrHozSubmissionFormView from "@pages/payroll/submission/submissionTypes/PrHozSubmissionFormView";
import { isNewEntity } from "@utils/definition.utils";
import { forEachKey } from "@utils/general";
import i18next from "i18next";
import { cloneDeep } from "lodash";

import { IAppContext } from "../../../../contexts/appContext/AppContext.types";
import { BasicInputSizes, FieldType, GroupedField } from "../../../../enums";
import { addFormDef, addFormGroup, getFormGroup } from "../../../../views/formView/Form.utils";

export const getDefinitions: IGetDefinition = (context: IAppContext, args?: IGetSubmissionDefArgs): IDefinition => {
    const d = cloneDeep(getSubmissionFormDef(PrHozSubmissionFormView, context, {
        ...args,
        type: PrSubmissionTypeCode.EmployersMassHealthInsuranceNotification,
        subType: null
    }));

    d.form.additionalProperties = [
        ...d.form.additionalProperties,
        { id: createPath(PrHozSubmissionEntity.Detail, PrHozSubmissionDetailEntity.Company, PrHozSubmissionCompanyEntity.Company, CompanyEntity.Id) }
    ];

    // **************** Add company / employer fields ****************
    const pathToCompany = createPath(PrHozSubmissionEntity.Detail, PrHozSubmissionDetailEntity.Company);
    addFormDef(d, createPath(pathToCompany, PrHozSubmissionCompanyEntity.LegalName), {
        width: BasicInputSizes.XL
    });
    addFormDef(d, createPath(pathToCompany, PrHozSubmissionCompanyEntity.LegalNumber), {});

    const companyAddressFields = getAddressFields(pathToCompany, {}, {
        addHouseStreetNumber: true,
        withoutCountry: true,
        multiline: false
    });
    forEachKey(companyAddressFields, (fieldId) => {
        addFormDef(d, fieldId, companyAddressFields[fieldId]);
    });

    addFormDef(d, createPath(pathToCompany, PrHozSubmissionCompanyEntity.InsurancePayerNumber), {});
    addFormDef(d, createPath(pathToCompany, PrHozSubmissionCompanyEntity.PhoneNumber), {
        ...PhoneNumberDef
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.Employer,
        title: i18next.t("Submission:Form.EmployerIdentifier"),
        rows: [[
            { id: createPath(pathToCompany, PrHozSubmissionCompanyEntity.LegalName) },
            { id: createPath(pathToCompany, PrHozSubmissionCompanyEntity.LegalNumber) },
            ...(getAddressGroup(pathToCompany, {
                multiline: false,
                withoutCountry: true,
                addHouseStreetNumber: true
            })[0]),
            { id: createPath(pathToCompany, PrHozSubmissionCompanyEntity.InsurancePayerNumber) },
            { id: createPath(pathToCompany, PrHozSubmissionCompanyEntity.PhoneNumber) },
        ]]
    });

    // **************** Add Employee related fields ****************
    const pathToEmployee = PrHozSubmissionEntity.Employee;

    addFormDef(d, createPath(pathToEmployee, PrHozSubmissionEmployeeEntity.HealthInsuranceCompany), {
        type: FieldType.ComboBox,
        label: i18next.t("Submission:PppzForm.HealthInsuranceCompany"),
        width: BasicInputSizes.L,
        isReadOnly: not(isNewEntity),
        fieldSettings: {
            displayName: PrHealthInsuranceCompanyEntity.ShortName,
            shouldDisplayAdditionalColumns: true,
        },
        additionalProperties: [{ id: PrHealthInsuranceCompanyEntity.Name }],
        columns: [
            { id: PrHealthInsuranceCompanyEntity.ShortName },
            { id: PrHealthInsuranceCompanyEntity.Name }
        ]
    });

    addFormDef(d, createPath(pathToEmployee, PrCSSZSubmissionEmployeeEntity.FirstName), {
        groupedField: GroupedField.MultiStart,
        isRequired: true
    });
    addFormDef(d, createPath(pathToEmployee, PrCSSZSubmissionEmployeeEntity.LastName), {
        groupedField: GroupedField.MultiEnd,
        isRequired: true
    });

    addFormDef(d, createPath(pathToEmployee, PrCSSZSubmissionEmployeeEntity.PersonalIdentificationNumber), {
        label: i18next.t("Submission:HozForm.PersonalIdentificationNumber")
    });

    const permanentAddressPath = createPath(pathToEmployee, PrHozSubmissionEmployeeEntity.PermanentAddress);
    const addressFields = getAddressFields(permanentAddressPath, {}, {
        addHouseStreetNumber: true,
        multiline: false
    });

    forEachKey(addressFields, (fieldId) => {
        addFormDef(d, fieldId, addressFields[fieldId]);
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.Employee,
        title: i18next.t("Submission:Form.EmployeeIdentifier"),
        rows: [[
            { id: createPath(pathToEmployee, PrCSSZSubmissionEmployeeEntity.FirstName) },
            { id: createPath(pathToEmployee, PrCSSZSubmissionEmployeeEntity.LastName) },
            { id: createPath(pathToEmployee, PrHozSubmissionEmployeeEntity.HealthInsuranceCompany) },
            { id: createPath(pathToEmployee, PrCSSZSubmissionEmployeeEntity.PersonalIdentificationNumber) },
        ], [
            ...(getAddressGroup(permanentAddressPath, {
                multiline: false,
                addHouseStreetNumber: true
            })[0])
        ]]
    });

    /* enhance main group with line items */
    // **************** Working intervals (line items) ****************
    addFormDef(d, createPath(PrHozSubmissionEntity.Detail, PrHozSubmissionDetailEntity.Items, PrHozSubmissionItemEntity.Change), {
        type: FieldType.ComboBox,
        label: i18next.t("Submission:HozForm.Changes"),
        width: BasicInputSizes.XL,
        fieldSettings: {
            displayName: PrHozChangeCodeEntity.Code,
            shouldDisplayAdditionalColumns: true,
        },
        additionalProperties: [{ id: PrHozChangeCodeEntity.Name }],
        columns: [
            { id: PrHozChangeCodeEntity.Code },
            { id: PrHozChangeCodeEntity.Name }
        ]
    });
    addFormDef(d, createPath(PrHozSubmissionEntity.Detail, PrHozSubmissionDetailEntity.Items, PrHozSubmissionItemEntity.DateChange), {});

    const mainGroup = getFormGroup(d, PrSubmissionGroup.Main);

    mainGroup.lineItems = {
        collection: createPath(PrHozSubmissionEntity.Detail, PrHozSubmissionDetailEntity.Items),
        order: PrHozSubmissionItemEntity.Id,
        canReorder: false,
        isItemCloneable: false,
        isTheOnlyItemRemovable: false,
        renderAsFastEntryForReadOnly: true,
        columns: [
            { id: PrHozSubmissionItemEntity.DateChange },
            { id: PrHozSubmissionItemEntity.Change },
        ],
    };

    return {
        ...d,
        entitySet: EntitySetName.PrHozSubmissions
    };
};

getDefinitions.translationFiles = [
    ...commonDef.translationFiles,
    ...CSSZTranslationFiles,
];
setDefByEntityType(EntityTypeName.PrHozSubmission, getDefinitions);